import { Card, Row, Col, Select, InputNumber, Alert, Typography, Statistic, Divider } from "antd";
import { useState } from "react";
import { normalizeTermOptions } from "../utils/loanUtils";

const { Text, Title } = Typography;

const LoanProductCalculator = ({ products }) => {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [amount, setAmount] = useState(10000);
  const [term, setTerm] = useState(1);
  const [customRate, setCustomRate] = useState(null);

  const calculateRepayment = () => {
    if (!selectedProduct || !amount) return null;

    const principal = amount;
    // Use custom rate if provided and allowed, otherwise use product's default rate
    const effectiveRate =
      (selectedProduct.allow_custom_interest_rate && customRate)
        ? customRate / 100
        : selectedProduct.interest_rate / 100;

    const time = term;

    // FV Formula: principal * (1 + rate)^nper
    if (selectedProduct.interest_type === "simple") {
      const expectedReturn = principal * (1 + effectiveRate * time);
      const profit = expectedReturn - principal;
      return { expectedReturn, profit, rate: effectiveRate * 100 };
    } else {
      // Compound interest calculation
      const periods = 12; // Monthly compounding
      const expectedReturn = principal * Math.pow(1 + effectiveRate / periods, periods * time);
      const profit = expectedReturn - principal;
      return { expectedReturn, profit, rate: effectiveRate * 100 };
    }
  };

  const result = calculateRepayment();

  return (
    <Card title="💰 Loan Calculator" style={{ marginTop: 24 }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <Text type="secondary">Loan Product</Text>
          <Select
            placeholder="Select Loan Product"
            onChange={(productId) => {
              const product = products.find((p) => p.id === productId);
              const termOptions = normalizeTermOptions(
                product?.loan_term_options
              );
              setSelectedProduct(product);
              setCustomRate(null); // Reset custom rate
              if (termOptions.length > 0) {
                setTerm(termOptions[0]);
              } else if (product?.loan_term) {
                setTerm(product.loan_term);
              }
            }}
            style={{ width: "100%", marginTop: 4 }}
            options={products.map((p) => ({
              label: p.loan_name,
              value: p.id,
            }))}
          />
        </Col>
        <Col xs={24} md={6}>
          <Text type="secondary">Amount Loaned (ZMW)</Text>
          <InputNumber
            style={{ width: "100%", marginTop: 4 }}
            prefix="ZMW"
            value={amount}
            onChange={setAmount}
            min={selectedProduct?.min_amount || 0}
            max={selectedProduct?.max_amount || 1000000}
            formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
          />
        </Col>
        <Col xs={24} md={6}>
          <Text type="secondary">Loan Term</Text>
          {(() => {
            const termOptions = normalizeTermOptions(
              selectedProduct?.loan_term_options
            );
            if (termOptions.length > 0) {
              return (
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={term}
                  onChange={setTerm}
                  placeholder="Select tenure"
                  options={termOptions.map((option) => ({
                    label: `${option} ${selectedProduct?.term_unit || ""}`.trim(),
                    value: option,
                  }))}
                />
              );
            }
            return (
              <InputNumber
                style={{ width: "100%", marginTop: 4 }}
                addonAfter={selectedProduct?.term_unit}
                value={term}
                onChange={setTerm}
                min={1}
              />
            );
          })()}
        </Col>
        {selectedProduct?.allow_custom_interest_rate && (
          <Col xs={24} md={6}>
            <Text type="secondary">
              Interest Rate (%) {selectedProduct.interest_rate_min && `[${selectedProduct.interest_rate_min}-${selectedProduct.interest_rate_max}%]`}
            </Text>
            <InputNumber
              style={{ width: "100%", marginTop: 4 }}
              placeholder={`Default: ${selectedProduct.interest_rate}%`}
              value={customRate}
              onChange={setCustomRate}
              min={selectedProduct.interest_rate_min}
              max={selectedProduct.interest_rate_max}
              step={0.1}
              formatter={(value) => (value ? `${value}%` : "")}
              parser={(value) => value.replace("%", "")}
            />
          </Col>
        )}
      </Row>

      {selectedProduct && result && (
        <>
          <Divider />
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Card bordered={false} style={{ background: "#f0f5ff" }}>
                <Statistic
                  title="Amount Loaned"
                  value={amount}
                  prefix="ZMW"
                  precision={2}
                  valueStyle={{ color: "#1890ff" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false} style={{ background: "#f6ffed" }}>
                <Statistic
                  title="Expected Return"
                  value={result.expectedReturn}
                  prefix="ZMW"
                  precision={2}
                  valueStyle={{ color: "#52c41a" }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card bordered={false} style={{ background: "#fffbe6" }}>
                <Statistic
                  title="Profit (Interest)"
                  value={result.profit}
                  prefix="ZMW"
                  precision={2}
                  valueStyle={{ color: "#faad14" }}
                />
              </Card>
            </Col>
          </Row>
          <div style={{ marginTop: 16, textAlign: "center" }}>
            <Text type="secondary">
              {selectedProduct.interest_type === "simple"
                ? `Simple Interest @ ${result.rate}% for ${term} ${selectedProduct.term_unit}`
                : `Compound Interest @ ${result.rate}% (${selectedProduct.compound_frequency} compounding)`}
            </Text>
            <br />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Formula: FV = Principal × (1 + Rate){term > 1 ? `^${term}` : ""}
            </Text>
          </div>
        </>
      )}
    </Card>
  );
};

export default LoanProductCalculator;
